import axios, { CancelTokenSource, AxiosRequestConfig } from 'axios'
import { isQA, isStaging } from '../../modules/firebase'
import { AdditionalFee, PricingResponse } from '@/types'

export type PricingFee = {
    baseSku: string
    baseSkuWithoutIdentifier: string
    count: number
}

/**
 * Migrates legacy fee format (id, sku) to new format (name, baseSku, baseSkuWithoutIdentifier).
 * Use when loading additionalFees from documents.
 */
export function migrateAdditionalFees(
    fees: Array<{
        id?: string
        sku?: string
        name?: string
        count: number
        baseSku?: string
        baseSkuWithoutIdentifier?: string
    }>,
    feeRecords: Array<{
        id?: string
        sku: string
        name: string
        baseSku: string
        baseSkuWithoutIdentifier: string
        recordId?: string
    }>,
): AdditionalFee[] {
    return fees.map(fee => {
        if (
            fee.baseSku != null &&
            fee.baseSkuWithoutIdentifier != null &&
            fee.name != null
        ) {
            return {
                name: fee.name,
                baseSku: fee.baseSku,
                baseSkuWithoutIdentifier: fee.baseSkuWithoutIdentifier,
                count: fee.count,
            }
        }
        const record = feeRecords.find(
            r => r.sku === fee.sku || r.id === fee.id || r.recordId === fee.id,
        )
        if (record) {
            return {
                name: record.name,
                baseSku: record.baseSku,
                baseSkuWithoutIdentifier: record.baseSkuWithoutIdentifier,
                count: fee.count,
            }
        }
        const sku = fee.sku ?? fee.id ?? 'unknown'
        return {
            name: fee.name ?? sku,
            baseSku: sku,
            baseSkuWithoutIdentifier: sku,
            count: fee.count,
        }
    })
}

/**
 * Maps AdditionalFee[] to the pricing API fee format.
 */
export function mapToPricingFees(
    fees: Array<{
        name: string
        baseSku: string
        baseSkuWithoutIdentifier: string
        count: number
    }>,
): PricingFee[] {
    return fees.map(fee => ({
        baseSku: fee.baseSku,
        baseSkuWithoutIdentifier: fee.baseSkuWithoutIdentifier,
        count: fee.count,
    }))
}

const PRICING_FUNCTION_URL = 'https://pricing-d2gswiyoaa-nw.a.run.app'
const STAGING_PRICING_FUNCTION_URL =
    'https://europe-west2-staging-9c37f.cloudfunctions.net/pricing'
const QA_PRICING_FUNCTION_URL = 'https://pricing-qa-d2gswiyoaa-nw.a.run.app'

export interface PricingRequestPayload {
    items: Array<{
        id: string
        count: number
        addToLoad?: boolean
    }>
    enquiryType?: string
    postcode?: string
    date?: string
    timeslot?: {
        id?: string
        slot?: string
        fee?: number
    } | null
    dismantledItems?: Array<{
        id: string
        count: number
    }> | null
    userId?: string
    manualAdjustment?: number
    discountCode?: string
    createdAt?: Date
    retailOverride?: unknown
    fees?: Array<{
        baseSku: string
        baseSkuWithoutIdentifier: string
        count: number
    }>
}

/**
 * Calls the pricing API with the provided payload
 * @param payload - The pricing request payload
 * @param cancelTokenSource - Optional cancel token source for request cancellation
 * @returns Promise resolving to the pricing response
 */
export async function callPricingAPI(
    payload: PricingRequestPayload,
    cancelTokenSource?: CancelTokenSource,
): Promise<PricingResponse> {
    const url = isQA
        ? QA_PRICING_FUNCTION_URL
        : isStaging
          ? STAGING_PRICING_FUNCTION_URL
          : PRICING_FUNCTION_URL

    const config: AxiosRequestConfig = {}

    if (cancelTokenSource) {
        config.cancelToken = cancelTokenSource.token
    }

    const { data } = await axios.post<PricingResponse>(url, payload, config)

    return data
}
